<script>
import Layout from "@/router/layouts/main";
import Breadcrumb from "@/components/Breadcrumb";
import { api } from "@/state/services";
import { required, maxLength } from "vuelidate/lib/validators";

// Tipos e tamanho aceitos no anexo (mesma regra da API)
const ALLOWED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "doc", "docx", "xls", "xlsx"];
const MAX_SIZE = 10 * 1024 * 1024;

export default {
  components: {
    Layout,
    Breadcrumb,
  },
  data() {
    return {
      categories: [],
      loading: false,
      sent: null,
      fileError: "",
      form: {
        category: null,
        subject: "",
        description: "",
        file: null,
      },
    };
  },
  validations: {
    form: {
      category: { required },
      subject: { required, maxLength: maxLength(150) },
      description: { required },
    },
  },
  computed: {
    categoryOptions() {
      return [{ value: null, text: "Selecione a categoria", disabled: true }].concat(
        this.categories.map((row) => ({ value: row.id, text: row.name }))
      );
    },
  },
  methods: {
    getCategories() {
      api.get("ticket/categories").then((response) => {
        if (response.data.status == "success") {
          this.categories = response.data.categories;
        }
      });
    },
    checkFile(file) {
      this.fileError = "";
      if (!file) {
        return true;
      }

      const extension = String(file.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(extension)) {
        this.fileError = "Use PDF, JPG, PNG, DOC, DOCX, XLS ou XLSX.";
        return false;
      }
      if (file.size > MAX_SIZE) {
        this.fileError = "O arquivo deve ter até 10 MB.";
        return false;
      }
      return true;
    },
    submit() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid || !this.checkFile(this.form.file)) {
        return;
      }

      const formData = new FormData();
      formData.append("category", this.form.category);
      formData.append("subject", this.form.subject);
      formData.append("description", this.form.description);
      if (this.form.file) {
        formData.append("file", this.form.file);
      }

      this.loading = true;
      api
        .post("ticket", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          if (response.data.status == "success") {
            this.sent = response.data.ticket;
          } else {
            this.$toast.error(response.data.message || "Não foi possível enviar a solicitação, tente novamente.");
          }
        })
        .catch(() => {
          this.$toast.error("Não foi possível enviar a solicitação, tente novamente.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  mounted() {
    this.getCategories();
  },
};
</script>

<template>
  <Layout>
    <Breadcrumb :items="[{ text: 'Ajuda' }, { text: 'Fale Conosco', to: '/contact' }, { text: 'Nova solicitação' }]" />
    <div class="row">
      <div class="col-12">
        <div class="page-title-box d-flex justify-content-between align-items-center">
          <h3 class="mb-0">{{ sent ? "Solicitação enviada!" : "Nova solicitação" }}</h3>
          <router-link to="/contact" class="btn btn-default btn-sm">Voltar</router-link>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-lg-8">
        <div v-if="sent" class="card">
          <div class="card-body text-center py-5">
            <i class="bx bx-check-circle text-success display-4"></i>
            <p class="mt-3 mb-1">Protocolo</p>
            <h2 class="notranslate">{{ sent.protocol }}</h2>
            <p class="text-muted">Anote o número {{ sent.protocol }}; também enviamos por e-mail.</p>
            <router-link :to="'/contact/' + sent.id" class="btn btn-default text-uppercase">Acompanhar solicitação</router-link>
          </div>
        </div>

        <div v-else class="card">
          <div class="card-body">
            <b-form @submit.prevent="submit">
              <b-form-group label="Categoria *" label-for="contact-category">
                <b-form-select
                  id="contact-category"
                  v-model="form.category"
                  :options="categoryOptions"
                  :class="{ 'is-invalid': $v.form.category.$error }"
                ></b-form-select>
                <div v-if="$v.form.category.$error" class="invalid-feedback">Selecione a categoria.</div>
              </b-form-group>

              <b-form-group label="Assunto *" label-for="contact-subject">
                <b-form-input
                  id="contact-subject"
                  v-model="form.subject"
                  type="text"
                  maxlength="150"
                  autocomplete="off"
                  :class="{ 'is-invalid': $v.form.subject.$error }"
                ></b-form-input>
                <div v-if="$v.form.subject.$error" class="invalid-feedback">Informe o assunto.</div>
              </b-form-group>

              <b-form-group label="Descrição *" label-for="contact-description">
                <b-form-textarea
                  id="contact-description"
                  v-model="form.description"
                  rows="6"
                  :class="{ 'is-invalid': $v.form.description.$error }"
                ></b-form-textarea>
                <div v-if="$v.form.description.$error" class="invalid-feedback">Descreva a sua solicitação.</div>
              </b-form-group>

              <b-form-group label="Anexo" label-for="contact-file" description="Opcional. Um arquivo: PDF, JPG, PNG, DOC, DOCX, XLS ou XLSX (até 10 MB).">
                <b-form-file
                  id="contact-file"
                  v-model="form.file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  placeholder="Escolher arquivo"
                  browse-text="Procurar"
                  :state="fileError ? false : null"
                  @input="checkFile"
                ></b-form-file>
                <div v-if="fileError" class="text-danger small mt-1">{{ fileError }}</div>
              </b-form-group>

              <div class="text-right">
                <b-button type="submit" variant="default" :disabled="loading">
                  Enviar
                  <b-spinner v-if="loading" small class="ml-2 align-middle" role="status"></b-spinner>
                </b-button>
              </div>
            </b-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
